import { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Chip, Avatar, Link, Tooltip } from '@mui/material';
import { format } from 'date-fns';
import { formatPaymentDate } from './formatPaymentDate';
import {
    CloudDownload as CloudDownloadIcon,
    CalendarMonth as RentingIcon,
    EmojiEvents as TournamentIcon,
    SportsEsports as GameIcon,
    School as ClassIcon,
    ShoppingCart as SalePointIcon,
    AccountBalanceWallet as PurseIcon,
    CardMembership as AbonadosIcon,
    ConfirmationNumber as BonoIcon,
} from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttButtonFab,
    SttChipUser,
    SttTopControls,
    SttTable,
    SttPage,
} from '../../../sporttia/all';
import { useInteractionsFiles, useLoader, useToggle } from '../../../lib/hooks';
import PaymentModal from '../../../components/paymentModal/PaymentModal';
import PaymentCreationDialog from '../../../components/dialogs/PaymentCreationDialog';
import { fullName, subsetObject } from '../../../lib/utils';
import Q60DownloadDialog from '../../../components/dialogs/Q60DownloadDialog';
import Mship from '../mships/Mship';
import DiscountTooltip from '../../../components/discounts/DiscountTooltip';
import DateBuilder from '../../../lib/DateBuilder';
import type { Payment } from '../../../types/models/Payment';
import { PERMISSIONS } from '../../../lib/permissions';

type FilterConfig = {
    concept: string;
    userName: string;
    subpayments: boolean;
    usePeriodEnabled: boolean;
    paymentPeriodEnabled: boolean;
    useIni: string;
    useEnd: string;
    paidIni: string;
    paidEnd: string;
    paymentForms: string;
    withDiscounts: boolean;
    status?: string;
    modules?: string;
    idCreator?: number;
    idCollector?: number;
};

const getTodayISO = () => format(new Date(), 'yyyy-MM-dd');

const filterConfigurations: Record<string, FilterConfig> = {
    NORMAL: {
        concept: '',
        userName: '',
        subpayments: false,
        usePeriodEnabled: false,
        paymentPeriodEnabled: false,
        useIni: getTodayISO(),
        useEnd: getTodayISO(),
        paidIni: getTodayISO(),
        paidEnd: getTodayISO(),
        paymentForms: '',
        withDiscounts: false,
    },
    CASH_CONTROL: {
        concept: '',
        userName: '',
        subpayments: false,
        usePeriodEnabled: false,
        paymentPeriodEnabled: true,
        useIni: getTodayISO(),
        useEnd: getTodayISO(),
        paidIni: getTodayISO(),
        paidEnd: getTodayISO(),
        paymentForms:
            'CASH,DATAFONO,DATAFONO_CONNECTED,TPV,BANK,RECEIPT,CASHDRO',
        withDiscounts: false,
    },
    PURSE: {
        concept: '',
        userName: '',
        subpayments: false,
        usePeriodEnabled: false,
        paymentPeriodEnabled: true,
        useIni: getTodayISO(),
        useEnd: getTodayISO(),
        paidIni: getTodayISO(),
        paidEnd: getTodayISO(),
        paymentForms: 'PURSE',
        withDiscounts: false,
    },
};

// TODO No está realizado de la mejor forma, hay que cambiar en el futuro, chapuza para entrega rápida.
const SCsWithXMLPermissions = ['2525', '3042', '3271', '3128'];

const moduleIconConfig: Record<
    string,
    { icon: React.ElementType; color: string }
> = {
    RENTING: { icon: RentingIcon, color: '#ff5353' },
    TOURNAMENT: { icon: TournamentIcon, color: '#19cc3a' },
    EVENT: { icon: TournamentIcon, color: '#19cc3a' },
    GAME: { icon: GameIcon, color: '#4768fd' },
    CLASS: { icon: ClassIcon, color: '#fd66bc' },
    SALE_POINT: { icon: SalePointIcon, color: '#8a6d3b' },
    PURSE: { icon: PurseIcon, color: '#3186a7' },
    ABONADOS: { icon: AbonadosIcon, color: '#8a3930' },
    BUY_ABONO: { icon: AbonadosIcon, color: '#8a3930' },
    BONO: { icon: BonoIcon, color: '#269c97' },
};

const getParamsToAPI = (filterParam: FilterConfig) => {
    let params: Partial<FilterConfig> = subsetObject(
        filterParam,
        'concept,subpayments,userName,modules,status,idCreator,idCollector,withDiscounts',
    );

    if (params.modules === '') {
        delete params.modules;
    }

    if (filterParam.paymentForms !== '') {
        const paymentsType = filterParam.paymentForms.replace('PENDING', '');

        params = {
            ...params,
            paymentForms: paymentsType,
        };
    }

    // Add paid period
    if (filterParam.paymentPeriodEnabled) {
        params.paidIni = filterParam.paidIni;
        params.paidEnd = filterParam.paidEnd;
    }

    // Add use period
    if (filterParam.usePeriodEnabled) {
        params.useIni = filterParam.useIni;
        params.useEnd = filterParam.useEnd;
    }

    return params;
};

type PaymentsResponse = {
    rows: Payment[];
    count: number;
    summatory?: {
        total: number;
    };
};

type Admin = {
    id: number;
    name: string;
};

/*
 * Página de cobros /sc/payments.
 */
export default function Payments(): React.ReactElement {
    const { t } = useTranslation();
    const cxt = useContext(AppContext)!;
    const { downloadFile } = useInteractionsFiles();
    const { id: urlId } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [payments, setPayments] = useState<PaymentsResponse | null>(null);
    const [paymentId, setPaymentId] = useState<number | null>(null);
    const [preset, setPreset] = useState<string>('CASH_CONTROL');
    const [admins, setAdmins] = useState<Admin[]>([]);
    const [creatingPayment, setCreatingPayment] = useState<boolean>(false);
    const [filters, setFilters] = useState<FilterConfig>(() => {
        const config =
            filterConfigurations.CASH_CONTROL || filterConfigurations.NORMAL;
        if (!config) {
            throw new Error('No filter configuration available');
        }
        return config;
    });
    const [loadingData, setLoadingData] = useState<boolean>(false);
    const [openQ60Dialog, setOpenQ60Dialog] = useState<boolean>(false);
    const [selectedMship, setSelectedMship] = useState<{ id: number } | null>(
        null,
    );

    const [trash, toggleTrash] = useToggle(() => {
        loadPayments();
    });

    // Handle payment ID from URL path parameter (e.g., /payments/123)
    useEffect(() => {
        if (urlId) {
            setPaymentId(Number(urlId));
        }
    }, [urlId]);

    /**
     * Handle close of payment modal
     */
    const handlePaymentClose = () => {
        setPaymentId(null);
        // Navigate back to payments list if we came from a direct URL
        if (urlId) {
            navigate('/payments');
        }
    };

    /**
     * Init
     *
     * Title & load administrators (used in 2 different filter fields)
     */

    /**
     * Accepts an optional parameter 'fltr' to enforce a set of filters regardless of 'filters' state. Used to enforce filterConfigurations.
     */
    function loadPayments(
        p = { page: 1, rows: 20 },
        filterParam: FilterConfig = filters,
    ) {
        setLoadingData(true);
        const params = getParamsToAPI(filterParam);

        // Call API
        cxt?.api('GET', `/scs/${cxt?.sc?.id}/payments`, {
            params: {
                ...params,
                summatory: true,
                trash,
                page: p.page,
                rows: p.rows,
            },
            success: (r: PaymentsResponse) => {
                setPayments(r);
                setLoadingData(false);
            },
        });
    }

    // If the preset changes, we must save the filters AND request the data manually
    useEffect(() => {
        const newFilters =
            filterConfigurations[preset] || filterConfigurations.NORMAL;
        if (newFilters) {
            setFilters(newFilters);
            loadPayments(undefined, newFilters);
        }
    }, [preset]);

    /**
     * Convenient because we'll use the list of admins on 2 different filter fields that are rendered simultaneously and are thus prone to the 404 API bug
     */
    function loadAdmins() {
        cxt?.api('GET', `/scs/${cxt?.sc?.id}/admins`, {
            success: (response: { rows: Admin[] }) => setAdmins(response?.rows),
        });
    }

    useEffect(() => {
        loadAdmins();
    }, []);

    const generateORGTFilesMutation = useMutation({
        mutationFn: (bodyParams: any) =>
            Promise.resolve(
                cxt?.api('POST', '/files/sgttxs', { params: bodyParams }),
            ),
        onSuccess: () => {
            cxt?.showMessage('S', t('page.sc.collections.orgt.success'));
        },
        onError: () => {
            cxt?.showMessage('E', t('page.sc.collections.orgt.error'));
        },
    });

    function download(format: string) {
        const params = getParamsToAPI(filters);
        let type = '';
        let downloadURL = '';

        switch (format) {
            case 'pdf':
                type = 'application/pdf';
                break;
            case 'csv':
                type = 'text/csv';
                break;
            case 'xml':
                type = 'text/xml';
                break;
            default:
                break;
        }

        if (format === 'xml') {
            downloadURL = `/scs/${cxt?.sc?.id}/payments/navision.xml`;
        } else {
            downloadURL = `/scs/${cxt?.sc?.id}/payments.${format}`;
        }

        downloadFile(downloadURL, type, params, `${t('Payments')}.${format}`);
    }

    function updatePayment(payment: Payment) {
        if (payment && payments) {
            setPayments({
                ...payments,
                rows: payments.rows.map((p: any) =>
                    p.id === payment.id ? payment : p,
                ),
            });
        }
    }

    function deletePayment(payment: Payment) {
        if (payment && payments) {
            setPayments({
                ...payments,
                count: payments.count - 1,
                rows: payments.rows.filter((p: any) => p.id !== payment.id),
            });
        }
    }

    const formatPriceValue = (
        value: number | string | null | undefined,
        defaultValue = 0,
    ): number => {
        if (value === null || value === undefined || value === '') {
            return defaultValue;
        }
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        return Number.isNaN(numValue) ? defaultValue : numValue;
    };

    const loadingStates = generateORGTFilesMutation?.isPending
        ? ['loading']
        : [];
    const [, loader] = useLoader(loadingStates);
    return (
        <>
            {loader}
            <SttPage title={t('Collections')}>
                <SttTopControls
                    p={2}
                    onToggleTrash={toggleTrash}
                    fields={[
                        {
                            xs: 12,
                            sm: 6,
                            md: 4,
                            lg: 3,
                            caption: t('Concept'),
                            type: 'text',
                            name: 'concept',
                            value: filters.concept,
                            onEnterKey: () => loadPayments(),
                        },
                    ]}
                    extraFields={[
                        // User name
                        {
                            xs: 12,
                            sm: 6,
                            md: 6,
                            lg: 6,
                            caption: t('User'),
                            name: 'userName',
                            type: 'text',
                            value: filters.userName,
                            onEnterKey: () => {},
                        },
                        // Payment status
                        {
                            xs: 12,
                            sm: 6,
                            md: 6,
                            lg: 6,
                            caption: t('Status'),
                            name: 'status',
                            type: 'selectAutocomplete',
                            value: filters.status,
                            labelField: 'label',
                            valueField: 'id',
                            options: cxt?.constants?.paymentStatuses || [],
                        },
                        // Date of use
                        {
                            xs: 12,
                            sm: 12,
                            md: 6,
                            lg: 6,
                            caption: t('DateOfUse'),
                            name: 'usePeriod',
                            type: 'period',
                            enabled: filters.usePeriodEnabled,
                            nameIni: 'useIni',
                            valIni: filters.useIni,
                            nameEnd: 'useEnd',
                            valEnd: filters.useEnd,
                            onToggle: (value: boolean) =>
                                setFilters({
                                    ...filters,
                                    usePeriodEnabled: value,
                                }),
                        },
                        // Date of payment
                        {
                            xs: 12,
                            sm: 12,
                            md: 6,
                            lg: 6,
                            caption: t('DateOfPayment'),
                            name: 'paymentPeriod',
                            type: 'period',
                            enabled: filters.paymentPeriodEnabled,
                            nameIni: 'paidIni',
                            valIni: filters.paidIni,
                            nameEnd: 'paidEnd',
                            valEnd: filters.paidEnd,
                            onToggle: (value: boolean) =>
                                setFilters({
                                    ...filters,
                                    paymentPeriodEnabled: value,
                                }),
                        },
                        // Payment form
                        {
                            xs: 12,
                            sm: 12,
                            md: 12,
                            lg: 12,
                            caption: t('PaymentForm'),
                            name: 'paymentForms',
                            type: 'selectMulti',
                            value: filters.paymentForms,
                            options:
                                cxt?.constants && cxt?.constants?.paymentForms
                                    ? cxt?.constants?.paymentForms.map(
                                          (pf: any) => ({
                                              label: pf.label,
                                              value:
                                                  pf.id === ''
                                                      ? 'PENDING'
                                                      : pf.id,
                                          }),
                                      )
                                    : [],
                        },
                        // Creator (user)
                        {
                            xs: 12,
                            sm: 12,
                            md: 12,
                            lg: 12,
                            caption: t('Creator'),
                            name: 'idCreator',
                            type: 'selectAutocomplete',
                            value: filters.idCreator,
                            labelField: 'name',
                            valueField: 'id',
                            options: admins,
                        },
                        // Collector (user)
                        {
                            xs: 12,
                            sm: 12,
                            md: 12,
                            lg: 12,
                            caption: t('Collector'),
                            name: 'idCollector',
                            type: 'selectAutocomplete',
                            value: filters.idCollector,
                            labelField: 'name',
                            valueField: 'id',
                            options: admins,
                        },
                        // Sub payments
                        {
                            xs: 12,
                            sm: 12,
                            md: 12,
                            lg: 12,
                            caption: t('ChildPayments'),
                            name: 'subpayments',
                            type: 'check',
                            checked: !!filters.subpayments,
                            onChange: ({
                                name,
                                value,
                            }: {
                                name: string | undefined;
                                value: unknown;
                            }) => {
                                if (!name) return;
                                setFilters({
                                    ...filters,
                                    [name]: +(value as boolean),
                                });
                            },
                        },
                        // With discounts
                        {
                            xs: 12,
                            sm: 12,
                            md: 12,
                            lg: 12,
                            caption: t('Discounts'),
                            name: 'withDiscounts',
                            type: 'check',
                            checked: !!filters.withDiscounts,
                            onChange: ({
                                name,
                                value,
                            }: {
                                name: string | undefined;
                                value: unknown;
                            }) => {
                                if (!name) return;
                                setFilters({
                                    ...filters,
                                    [name]:
                                        name === 'withDiscounts'
                                            ? !!value
                                            : value,
                                });
                            },
                        },
                        // Module
                        {
                            xs: 12,
                            sm: 12,
                            md: 12,
                            lg: 12,
                            caption: t('Module'),
                            name: 'modules',
                            type: 'selectMulti',
                            value: filters.modules || '',
                            options: cxt?.constants?.modules
                                ? cxt.constants.modules.map((m: any) => {
                                      // El back end entiende que el valor del modulo para eventos es TOURNAMENT
                                      // EVENT falla ya que es un modulo que no está permitido
                                      if (m.id === 'EVENT') {
                                          return {
                                              label: m.label,
                                              value: 'TOURNAMENT',
                                          };
                                      }
                                      if (m.id === 'ABONADOS') {
                                          return {
                                              label: m.label,
                                              value: `${m.id},BUY_ABONO`,
                                          };
                                      }

                                      return {
                                          label: m.label,
                                          value: m.id,
                                      };
                                  })
                                : [],
                        },
                    ]}
                    menu={[
                        {
                            caption: `${t('Download')} PDF`,
                            onClick: () => download('pdf'),
                        },
                        {
                            caption: `${t('Download')} CSV`,
                            onClick: () => download('csv'),
                        },
                        {
                            caption: 'Q60 mod. 2',
                            onClick: () => setOpenQ60Dialog(true),
                        },
                        ((cxt?.sc?.id === 3444 || cxt?.sc?.id === 2401) && {
                            caption: `${t('Download')} ORGT`,
                            onClick: () => {
                                generateORGTFilesMutation.mutate({
                                    useIni: filters?.useIni,
                                    useEnd: filters?.useEnd,
                                    paymentForms: filters?.paymentForms,
                                    paymentStatus: filters?.status,
                                } as any);
                            },
                        }) as any,
                        ...(SCsWithXMLPermissions.includes(
                            localStorage.getItem('idSC') || '',
                        )
                            ? [
                                  {
                                      caption: `${t('Download')} XML`,
                                      onClick: () => download('xml'),
                                  },
                              ]
                            : []),
                        (cxt?.sc &&
                            cxt?.sc?.id === 2359 && {
                                caption: t('XabiaDocuments'),
                                onClick: () => setOpenQ60Dialog(true),
                            }) as any,
                    ].filter(Boolean)}
                    menuIcon={<CloudDownloadIcon />}
                    onChange={({
                        name,
                        value,
                    }: {
                        name: string | undefined;
                        value: any;
                    }) => {
                        if (!name) return;
                        setFilters({ ...filters, [name]: value });
                    }}
                    onFilter={loadPayments}
                    loadingData={loadingData}
                    mainAction={{
                        type: 'select',
                        minWidth: 180,
                        options: [
                            {
                                caption: t('Normal'),
                                value: 'NORMAL',
                            },
                            {
                                caption: t(
                                    'page.sc.collections.type.cashControl',
                                ),
                                value: 'CASH_CONTROL',
                            },
                            {
                                caption: t('page.sc.collections.type.purse'),
                                value: 'PURSE',
                            },
                        ],
                        selected: preset,
                        onChange: (value: string) => setPreset(value),
                    }}
                />

                <SttTable
                    data={payments}
                    loading={loadingData}
                    perPage={20}
                    columns={[
                        {
                            title: t('Created'),
                            align: 'center',
                            value: (row: Payment) => (
                                <>
                                    <SttChipUser user={row.creator as any} />
                                    <Box mt={1}>
                                        {new DateBuilder(
                                            row.created,
                                            cxt?.sc?.timezone?.name,
                                            cxt?.lang?.key,
                                        ).dmy()}
                                    </Box>
                                </>
                            ),
                        },
                        {
                            title: t('Module'),
                            align: 'center',
                            width: 50,
                            value: (row: Payment) => {
                                if (!row.module) return null;
                                const config = moduleIconConfig[row.module];
                                if (!config) return null;
                                const IconComponent = config.icon;
                                return (
                                    <Tooltip
                                        title={t(
                                            `modules.${row.module.toLowerCase()}`,
                                        )}
                                    >
                                        <IconComponent
                                            sx={{
                                                color: config.color,
                                                fontSize: 22,
                                            }}
                                        />
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            title: t('Concept'),
                            align: 'left',
                            value: (row: Payment) => (
                                <>
                                    <Link
                                        onClick={() => setPaymentId(row.id)}
                                        sx={{
                                            color: 'var(--brand-text)',
                                            textDecoration: 'none',
                                            cursor: 'pointer',
                                            fontWeight: 'bold',
                                            '&:hover': {
                                                textDecoration: 'underline',
                                            },
                                        }}
                                    >
                                        {row.concept}
                                    </Link>

                                    {row.user?.mship && (
                                        <Box mt={0.5}>
                                            <Link
                                                onClick={() =>
                                                    setSelectedMship(
                                                        row.user!.mship,
                                                    )
                                                }
                                                sx={{
                                                    color: 'text.secondary',
                                                    textDecoration: 'none',
                                                    cursor: 'pointer',
                                                    fontSize: '0.85rem',
                                                    '&:hover': {
                                                        textDecoration:
                                                            'underline',
                                                    },
                                                }}
                                            >
                                                {fullName(row.user as any)}
                                            </Link>
                                        </Box>
                                    )}

                                    {row.children && (
                                        <Box mt={1}>
                                            <Chip
                                                size="small"
                                                avatar={
                                                    <Avatar>
                                                        {row.children.count}
                                                    </Avatar>
                                                }
                                                label={t('Children')}
                                            />
                                        </Box>
                                    )}

                                    {row.parent && (
                                        <Box mt={1}>
                                            <Chip
                                                size="small"
                                                label={t('Father')}
                                            />
                                        </Box>
                                    )}

                                    {row.discounts && (
                                        <DiscountTooltip
                                            discounts={row.discounts}
                                        />
                                    )}
                                </>
                            ),
                        },
                        {
                            title: t('Period'),
                            align: 'center',
                            value: (row: Payment) => {
                                if (!row.ini) return null;
                                const iniDate = formatPaymentDate(row.ini);
                                if (!row.end || row.ini === row.end) {
                                    return iniDate;
                                }
                                const endDate = formatPaymentDate(row.end);
                                return (
                                    <Box>
                                        <Box>{iniDate}</Box>
                                        <Box>{endDate}</Box>
                                    </Box>
                                );
                            },
                        },
                        {
                            title: t('Paid'),
                            type: 'text',
                            value: (row: Payment) =>
                                row.paymentDate && (
                                    <>
                                        <SttChipUser
                                            user={row.collector as any}
                                        />
                                        <Box mt={1}>
                                            {formatPaymentDate(row.paymentDate)}
                                        </Box>
                                    </>
                                ),
                        },
                        {
                            title: t('PForm'),
                            type: 'pf',
                            field: 'paymentForm',
                        },
                        {
                            title: t('Price'),
                            align: 'right',
                            type: 'price',
                            field: 'price',
                            value: (row: Payment) => ({
                                paid: row.status === 'PAID',
                                price: formatPriceValue(row.price),
                                priceShape: row.priceShape,
                            }),
                        },
                    ]}
                    totals={
                        payments && payments.summatory
                            ? [
                                  {
                                      index: 6,
                                      value: formatPriceValue(
                                          payments.summatory.total,
                                      ),
                                      adornment: cxt?.sc?.city?.country
                                          ?.currencySymbol
                                          ? ` ${cxt.sc?.city.country.currencySymbol}`
                                          : ' €',
                                  },
                              ]
                            : undefined
                    }
                    // Llamamos a la autoCarga con el 'preset' inicial
                    onFetch={loadPayments}
                />

                {paymentId && (
                    <PaymentModal
                        idPayment={paymentId}
                        onClose={handlePaymentClose}
                        onPay={updatePayment}
                        onRecovered={updatePayment}
                        onRefund={() => loadPayments()}
                        onDeleted={deletePayment}
                        onSave={updatePayment}
                        disableUrlNavigation={false}
                    />
                )}

                <PaymentCreationDialog
                    open={creatingPayment}
                    onPaymentSuccess={() => {
                        setCreatingPayment(false);
                        loadPayments();
                    }}
                    onClose={() => setCreatingPayment(false)}
                />

                <Q60DownloadDialog
                    open={openQ60Dialog}
                    onClose={() => setOpenQ60Dialog(false)}
                    filters={getParamsToAPI(filters)}
                />

                {selectedMship && (
                    <Mship
                        id={selectedMship.id}
                        open
                        onClose={() => setSelectedMship(null)}
                        disableUrlNavigation
                    />
                )}

                <SttButtonFab
                    onClick={() => setCreatingPayment(true)}
                    permission={PERMISSIONS.ACCOUNTING.CREATE}
                />
            </SttPage>
        </>
    );
}
